/* eslint-disable import/no-unresolved, import/no-extraneous-dependencies */
import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardActionArea from '@mui/material/CardActionArea';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { StreamingApp } from '../state/Apps';

export interface ApplicationCardProps {
  app: StreamingApp;
}

export default function ApplicationCard({ app }: ApplicationCardProps) {
  const [version, setVersion] = useState(app.latestVersion);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    event.preventDefault();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleVersionSelect = (
    event: React.MouseEvent,
    ver: StreamingApp['versions'][0]
  ) => {
    event.preventDefault();
    event.stopPropagation();
    setVersion(ver);
    handleClose();
  };

  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '3px 3px 5px #E8E8E8',
        border: '0.1rem solid #E8E8E8',
        borderRadius: 1,
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          transform: 'translateY(-2px)',
        },
      }}
    >
      <CardActionArea
        component="a"
        href={`/omniverse/app/${version.id}/sessions`}
        target="_blank"
        sx={{ height: '100%', display: 'flex', alignItems: 'stretch' }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'stretch',
            width: '100%',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              alignItems: 'flex-end',
              p: 2,
              flex: 1,
              overflow: 'hidden',
            }}
          >
            <Box
              component="img"
              src={app.icon}
              alt={app.title}
              title={app.title}
              sx={{
                width: 64,
                height: 64,
                objectFit: 'contain',
              }}
            />
            <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Typography variant="body2" color="text.secondary">
                {app.productArea}
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  fontSize: '1.25rem',
                  fontWeight: 500,
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  lineHeight: 1.5,
                  color: 'primary.main',
                }}
                title={app.title}
              >
                {app.title}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  fontSize: '0.75rem',
                  fontWeight: 500,
                  color: 'text.secondary',
                  alignSelf: 'flex-end',
                }}
              >
                {version.name}
              </Typography>
            </Box>
          </Box>

          <IconButton
            onClick={handleClick}
            sx={{
              backgroundColor: 'primary.main',
              color: 'white',
              borderRadius: 0,
              width: 32,
              height: 'auto',
              '&:hover': {
                backgroundColor: 'primary.dark',
              },
            }}
          >
            <KeyboardArrowDownIcon fontSize="small" />
          </IconButton>
        </Box>
      </CardActionArea>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {app.versions.map((ver) => (
          <MenuItem
            key={ver.id}
            onClick={(e) => handleVersionSelect(e, ver)}
            selected={ver.id === version.id}
          >
            {ver.name}
          </MenuItem>
        ))}
      </Menu>
    </Card>
  );
}
